import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
  InputBase,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Paper,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ShowChart as ShowChartIcon,
  GpsNotFixed as GpsNotFixedIcon,
  Notifications as NotificationsIcon,
  MenuBook as ImportContactsIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Search as SearchIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Receipt as ReceiptIcon,
  ManageSearch as ManageSearchIcon,
  DoneAll as DoneAllIcon,
  Paid as PaidIcon,
  Task as TaskIcon,
  LibraryAddCheck as LibraryAddCheckIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  SupervisedUserCircleOutlined,
} from "@mui/icons-material";
import TollIcon from "@mui/icons-material/Toll";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { logoutMutation, pairsQuery } from "../support/mutations";
import { debounce } from "@mui/material/utils";
import { ColorModeContext } from "../App";
import { useAtom } from "jotai";
import { currentUserAtom, logoutAtom } from "../support/atoms";
import { useNotification } from "../hooks/useNotification";
import { getRefreshToken } from "../support/graphqlApi";
import FullScreenSearch from "./FullScreenSearch";

const DRAWER_WIDTH = 250;
const COLLAPSED_DRAWER_WIDTH = 56;
const APPBAR_HEIGHT = 64;

const DashboardLayout = ({ children, slots = {} }) => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isHovering, setIsHovering] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [, logout] = useMutation(logoutMutation);
  const [, clearLoginCredentials] = useAtom(logoutAtom);
  const [localUser] = useAtom(currentUserAtom);
  const { showNotification } = useNotification();
  const isMounted = useRef(false);

  const handleDrawerToggle = () => {
    setIsNavOpen((prev) => !prev);
    if (isSearchOpen) {
      setIsSearchOpen(false);
    }
  };

  const handleNavigationClick = (path) => {
    handleSearchClose();
    if (window.innerWidth < 900) {
      setIsNavOpen(false);
    }
  };

  // Only update navigation state when search is closed and we have a pair
  useEffect(() => {
    if (!isSearchOpen && window.innerWidth >= 900) {
      setIsNavOpen(true);
    }
  }, [isSearchOpen]);

  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  // Check if we have a valid pair in the URL
  const hasValidPair = useMemo(() => {
    const pair = searchParams.get("pair");
    return pair && pair.length > 0;
  }, [searchParams]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout({ refreshToken: getRefreshToken() })
      .then((res) => {
        if (res.error) {
          showNotification(res.error.message, {
            severity: "error",
          });
        } else {
          clearLoginCredentials();
          showNotification("Logged out successfully", {
            severity: "success",
          });
        }
      })
      .catch((err) => {
        showNotification(err.message, {
          severity: "error",
        });
      });
  };

  const handleExpandClick = (path) => {
    setExpandedItems((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const navigation = [
    {
      label: "Chart",
      icon: <ShowChartIcon />,
      path: "chart",
    },
    // { title: "Contract", icon: <DescriptionIcon />, segment: "contract" },
    { kind: "divider" },
    {
      label: "Trackers",
      icon: <GpsNotFixedIcon />,
      path: "trackers",
      children: [
        {
          path: "txs",
          label: "Transactions",
          icon: <ReceiptIcon />,
        },
        {
          path: "keywords",
          label: "Keywords",
          icon: <ManageSearchIcon />,
        },
      ],
    },
    { label: "Tokens", icon: <TollIcon />, path: "tokens" },
    {
      label: "Events",
      icon: <NotificationsIcon />,
      path: "events",
      children: [
        {
          path: "approvals",
          label: "Approvals",
          icon: <DoneAllIcon />,
        },
        {
          path: "liquidity-on-deployments",
          label: "Liquidity on Deployments",
          icon: <PaidIcon />,
        },
        {
          path: "standard-tokens",
          label: "Standard Tokens",
          icon: <TaskIcon />,
        },
        {
          path: "/no-dev-tokens",
          label: "No-Dev Tokens",
          icon: <LibraryAddCheckIcon />,
        },
      ],
    },
    {
      label: "Address Book",
      icon: <ImportContactsIcon />,
      path: "address-book",
    },
    { kind: "divider" },
    { label: "Settings", icon: <SettingsIcon />, path: "settings" },
  ];
  if (localUser?.role === "ADMIN") {
    navigation.push({
      label: "Admin",
      icon: <AdminPanelSettingsIcon />,
      path: "admin",
      children: [
        {
          label: "Users",
          icon: <SupervisedUserCircleOutlined />,
          path: "users",
        },
      ],
    });
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        p: 0,
      }}
    >
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { xs: "100%", md: `calc(100% - ${COLLAPSED_DRAWER_WIDTH}px)` },
          ml: { xs: 0, md: `${COLLAPSED_DRAWER_WIDTH}px` },
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.2)"
              : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06)",
          zIndex: 1000,
          display: isSearchOpen ? "none" : "flex",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: APPBAR_HEIGHT,
            width: "100%",
            borderBottom: "none",
            pl: { md: 3, lg: 3, xl: 3 },
          }}
          disableGutters={true}
        >
          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              m: 1,
              display: { md: "none" },
              cursor: "pointer",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Search Bar */}
          <Box
            sx={{
              position: "relative",
              borderRadius: 1,
              width: "100%",
              maxWidth: 600,
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by token name, symbol, or address..."
              variant="outlined"
              size="small"
              onClick={() => setIsSearchOpen(true)}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{
                      ml: 1,
                      mr: 1,
                    }}
                  />
                ),
              }}
            />
          </Box>

          {/* User Profile */}
          <Box sx={{ display: "flex", alignItems: "center", m: 1 }}>
            <IconButton
              size="large"
              onClick={handleMenu}
              color="inherit"
              sx={{ p: 1 }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                }}
              >
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  colorMode.toggleColorMode();
                  handleClose();
                }}
              >
                <ListItemIcon>
                  {theme.palette.mode === "dark" ? (
                    <LightModeIcon fontSize="small" />
                  ) : (
                    <DarkModeIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <Typography variant="body2">
                  {theme.palette.mode === "dark" ? "Light Mode" : "Dark Mode"}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={async () => await handleLogout()}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Full Screen Search */}
      {isSearchOpen ? (
        <FullScreenSearch onClose={handleSearchClose} open={isSearchOpen} />
      ) : null}

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={isNavOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            height: APPBAR_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            component="img"
            src="/public/web-app-manifest-512x512.png"
            sx={{
              width: 32,
              height: 32,
              objectFit: "contain",
              objectPosition: "center",
              borderRadius: 1,
            }}
          />
        </Box>
        <List>
          {navigation.map((item, index) => {
            if (item.kind === "divider") {
              return <Divider key={index} sx={{ my: 1 }} />;
            }

            const isParentActive = location.pathname.startsWith(
              `/${item.path}`
            );
            const hasChildren = item.children && item.children.length > 0;

            return (
              <React.Fragment key={item.path || index}>
                <ListItem
                  component={hasChildren ? "div" : Link}
                  to={hasChildren ? undefined : item.path}
                  onClick={() => {
                    if (hasChildren) {
                      handleExpandClick(item.path);
                    } else {
                      handleNavigationClick(item.path);
                    }
                  }}
                  sx={{
                    color: isParentActive ? "primary.main" : "text.primary",
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === "dark" ? 0.24 : 0.12
                      ),
                      color: isParentActive ? "primary.main" : "text.primary",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isParentActive ? "primary.main" : "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                  {hasChildren &&
                    (expandedItems[item.path] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </ListItem>
                {hasChildren && (
                  <Collapse
                    in={expandedItems[item.path]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {item.children.map((child) => {
                        const isChildActive =
                          location.pathname === `/${item.path}/${child.path}`;
                        return (
                          <ListItem
                            key={child.path}
                            component={Link}
                            to={`${item.path}/${child.path}`}
                            onClick={() => handleNavigationClick(child.path)}
                            sx={{
                              pl: 4,
                              color: isChildActive
                                ? "primary.main"
                                : "text.secondary",
                              bgcolor: isChildActive
                                ? alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === "dark" ? 0.16 : 0.08
                                  )
                                : "transparent",
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  theme.palette.mode === "dark" ? 0.24 : 0.12
                                ),
                                color: "text.primary",
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 40,
                                color: isChildActive
                                  ? "primary.main"
                                  : "inherit",
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                fontSize: "0.875rem",
                                fontWeight: isChildActive ? 600 : 400,
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        sx={{
          display: { xs: "none", md: "block" },
          width: COLLAPSED_DRAWER_WIDTH,
          flexShrink: 0,
          position: "fixed",
          zIndex: 99999,
          "& .MuiDrawer-paper": {
            width: isHovering ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create(["width", "box-shadow"], {
              duration: theme.transitions.duration.standard,
            }),
            overflowX: "hidden",
            zIndex: 99999,
            height: "100%",
            position: "fixed",
            left: 0,
            top: 0,
            bgcolor: "background.paper",
          },
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            height: APPBAR_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            component="img"
            src="/public/web-app-manifest-512x512.png"
            sx={{
              width: 32,
              height: 32,
              objectFit: "contain",
              objectPosition: "center",
              borderRadius: 1,
            }}
          />
        </Box>

        {/* Navigation Items */}
        <List
          sx={{
            pt: 2,
            px: 0,
            overflowX: "hidden",
            position: "relative",
            zIndex: 99999,
          }}
        >
          {navigation.map((item, index) => {
            if (item.kind === "divider") {
              return <Divider key={index} sx={{ my: 1 }} />;
            }

            const isParentActive = location.pathname.startsWith(
              `/${item.path}`
            );
            const hasChildren = item.children && item.children.length > 0;

            return (
              <React.Fragment key={item.path || index}>
                <ListItem
                  component={hasChildren ? "div" : Link}
                  to={hasChildren ? undefined : item.path}
                  onClick={
                    hasChildren ? () => handleExpandClick(item.path) : undefined
                  }
                  sx={{
                    height: 40,
                    px: 0,
                    py: 0,
                    mx: 1,
                    mb: 0.5,
                    color: isParentActive ? "primary.main" : "text.secondary",
                    bgcolor: isParentActive
                      ? alpha(
                          theme.palette.primary.main,
                          theme.palette.mode === "dark" ? 0.16 : 0.08
                        )
                      : "transparent",
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === "dark" ? 0.24 : 0.12
                      ),
                      color: isParentActive ? "primary.main" : "text.primary",
                    },
                    cursor: "pointer",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    pr: 1,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: COLLAPSED_DRAWER_WIDTH - 16,
                      width: COLLAPSED_DRAWER_WIDTH - 16,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "inherit",
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                        transition: theme.transitions.create("color"),
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {isHovering && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                        justifyContent: "space-between",
                        pr: 1,
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        sx={{
                          m: 0,
                          pl: 1,
                          opacity: isHovering ? 1 : 0,
                          transition: theme.transitions.create([
                            "opacity",
                            "transform",
                          ]),
                          "& .MuiListItemText-primary": {
                            fontSize: "0.875rem",
                            fontWeight: isParentActive ? 600 : 500,
                          },
                        }}
                      />
                      {hasChildren && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: "inherit",
                            ml: 2,
                          }}
                        >
                          {expandedItems[item.path] ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </ListItem>
                {hasChildren && isHovering && (
                  <Collapse
                    in={expandedItems[item.path]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {item.children.map((child) => {
                        const isChildActive =
                          location.pathname === `/${item.path}/${child.path}`;
                        return (
                          <ListItem
                            key={child.path}
                            component={Link}
                            to={`${item.path}/${child.path}`}
                            sx={{
                              height: 32,
                              px: 0,
                              py: 0,
                              mx: 1,
                              mb: 0.5,
                              pl: 3,
                              color: isChildActive
                                ? "primary.main"
                                : "text.secondary",
                              bgcolor: isChildActive
                                ? alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === "dark" ? 0.16 : 0.08
                                  )
                                : "transparent",
                              borderRadius: 1,
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  theme.palette.mode === "dark" ? 0.24 : 0.12
                                ),
                                color: isChildActive
                                  ? "primary.main"
                                  : "text.primary",
                              },
                              textDecoration: "none",
                              whiteSpace: "nowrap",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 32,
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "inherit",
                                "& .MuiSvgIcon-root": {
                                  fontSize: 16,
                                  transition: theme.transitions.create("color"),
                                },
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.label}
                              sx={{
                                m: 0,
                                "& .MuiListItemText-primary": {
                                  fontSize: "0.8125rem",
                                  fontWeight: isChildActive ? 600 : 400,
                                },
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          padding: 2,
          paddingBottom: 0,
          width: { xs: "100%", md: `calc(100% - ${COLLAPSED_DRAWER_WIDTH}px)` },
          ml: { xs: 0, md: `${COLLAPSED_DRAWER_WIDTH}px` },
          mt: `${APPBAR_HEIGHT}px`,
          position: "relative",
          zIndex: "auto",
          height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
          overflow: "auto",
        }}
      >
        {slots.toolbarActions && (
          <Box sx={{ mb: 3 }}>{slots.toolbarActions}</Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout;
